import ClassNames from "classnames";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import { useFormik } from "formik";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import * as yup from "yup";
import AlertDialog from "../../components/AlertDialog";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import ButtonPill from "../../components/ButtonPill";
import DatePickerGroup from "../../components/DatePickerGroup";
import NotesInputGroup from "../../components/NotesInputGroup";
import NotesList from "../../components/NotesList";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import Select from "../../components/Select";
import Stepper from "../../components/Stepper";
import SubjectListInput from "../../components/SubjectListInput";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest, scorecardApiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import scrollToErrors from "../../utils/scrollToErrors";
import styles from "./SessionsForm.module.scss";

import {
  IsCampaignManager,
  IsCoordinator,
  IsNormalCoach,
} from "../../config/Roles";

import { LogsTable } from "../logs/LogsTable";

import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormHelperText from "@material-ui/core/FormHelperText";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import EqualizerIcon from "@material-ui/icons/Equalizer";
import CampaignList from "../../components/CampaignList";
import { useCampaign } from "../../context/campaign";

import { useTranslation } from "react-i18next";
import { Framework } from "../../config/Config";

dayjs.extend(utc);

// custom Hook to use previous value
function usePrevious(value) {
  const ref = React.useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function SessionsForm() {
  const history = useHistory();
  const { id } = useParams();
  const { selectedCampaign, setSelectedCampaign } = useCampaign();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [coacheeID, setCoacheeID] = useState("");
  const userRegion = getLocalValue("region");
  const [assignToOtherCoach, setAssignToOtherCoach] = useState(false);
  const [coachesList, setCoachesList] = useState([]);
  const [coacheesList, setCoacheesList] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [subjectSelectionsList, setSubjectSelectionsList] = useState([]);
  const [disableSubjects, setDisableSubjects] = useState(true);
  //const [noteList, setNoteList] = useState([]);
  const [noteCounter, setNoteCounter] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [viewStates, setViewStates] = useState(false);
  const [minReviewDate, setMinReviewDate] = useState(
    new Date().setDate(new Date().getDate() + 2)
  ); // default value = 2 days from now
  const [maxReviewDate, setMaxReviewDate] = useState(
    dayjs().add(1, "month").endOf("month").toDate()
  );

  const [formLocked, setFormLocked] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [disableDialogButtons, setDisableDialogButtons] = useState(false);
  const [savedFormState, setSavedFormState] = useState(null);
  const [selectedScorecard, setSelectedScorecard] = useState(null);
  // indicates whether it is on "update" or "create" mode
  const [isUpdate, setIsUpdate] = useState(id !== "new");
  const [hideProjectTargetKpi, sethideProjectTargetKpi] = useState(false);
  const [hideCurrentMetrics, sethideCurrentMetricsi] = useState(false);

  const initialForm = {
    session_type_id: "",
    session_datetime: new Date(),
    coach_id: getLocalValue("uid"),
    assigned_to: "",
    coachee_id: "",
    campaign_id: selectedCampaign,
    review_datetime: new Date().setDate(new Date().getDate() + 2), // default value = 2 days from now
    duration: sessionDuration,
    created_by: getLocalValue("uid"),
    method: "",
    framework: "",
    subjects: [],
    notes: [],
  };
  if (!localStorage.cerateSessionFormData) {
    localStorage.setItem("cerateSessionFormData", JSON.stringify(initialForm));
  }

  const localData =
    localStorage.cerateSessionFormData &&
      localStorage.cerateSessionFormData !== undefined
      ? JSON.parse(localStorage.cerateSessionFormData)
      : {};

  const tmpSubjects = [];
  if (localData.subjects && localData.subjects.length > 0) {
    for (const item of localData.subjects) {
      tmpSubjects.push({
        subject_id: item?.subject_id,
        metrics: item?.result_metrics,
        occurrence: item?.occurrence,
        link: item?.document_link,
        mtd_metric: item?.mtd_metric,
        project_target_kpi: item?.project_target_kpi,
      });
    }
  }
  const [subjectList, setSubjectList] = useState(tmpSubjects);

  const tmpNotes = [];
  if (localData.notes && localData.notes.length > 0) {
    for (const item of localData.notes) {
      tmpNotes.push({
        liked_by_coach: item?.liked_by_coach,
        liked_by_coachee: item?.liked_by_coachee,
        note_category_id: item?.note_category_id,
        id: noteCounter, // random counter value
        type_id: item?.note_category_id,
        note: item?.note,
        like: false, // messages are unliked by default
        author: getLocalValue("u_fullname"),
        note_created_at: new Date(),
      });
    }
  }
  const [noteList, setNoteList] = useState(tmpNotes);

  const [formState, setFormState] = useState({
    session_type_id: localData?.session_type_id
      ? localData.session_type_id
      : "",
    session_datetime:
      localData?.session_datetime &&
        localData?.session_datetime >= initialForm.session_datetime
        ? localData?.session_datetime
        : initialForm.session_datetime,
    coach_id: localData?.coach_id ? localData?.coach_id : getLocalValue("uid"),
    assigned_to: localData?.assigned_to ? localData?.assigned_to : "",
    coachee_id: localData?.coachee_id ? localData?.coachee_id : "",
    campaign_id:
      localData?.campaign_id && localData?.campaign_id !== ""
        ? localData?.campaign_id
        : selectedCampaign,
    review_datetime:
      localData?.review_datetime &&
        localData?.review_datetime >= initialForm.review_datetime
        ? localData?.review_datetime
        : initialForm.review_datetime, // default value = 2 days from now
    duration: localData?.duration ? localData?.duration : sessionDuration,
    created_by: localData?.created_by
      ? localData?.created_by
      : getLocalValue("uid"),
    method: localData?.method ? localData?.method : "",
    framework: localData?.framework ? localData?.framework : "",
    subjects: localData?.subjects ? localData?.subjects : [],
    notes: localData?.notes ? localData?.notes : [],
  });
  const [isPDFlow, setIsPDFlow] = useState(
    parseInt(localData?.session_type_id) === 2 ? true : false
  );

  const restFormValues = () => {
    localStorage.removeItem("cerateSessionFormData");
  };

  const validationSchema = {
    coacheeId: yup
      .string("Please select session coachee")
      .required(t("createSession.coacheeFieldMandatory")),
    method: yup
      .string("Select Mode")
      .required(t("createSession.modeIsMandatory")),
    framework: yup
      .string("Please select Framework")
      .required(t("createSession.frameworkIsMandatory")),
    session_type_id: yup
      .string("Please select Session type")
      .required(t("createSession.sessionTypeIsMandatory")),
    subjects: yup
      .array()
      .when("framework", {
        is: "1",
        then: yup.array().of(
          yup.object({
            subject_id: yup
              .string()
              .required("Please add a subject"),
            result_metrics: yup
              .string()
              .required("Please add subject metric to input"),
          })
        ),
      })
      .when("framework", {
        is: "2",
        then: yup.array().of(
          yup.object({
            subject_id: yup
              .string()
              .required("Please add a subject"),
          })
        ),
      })
      .min(1, "Please add a subject")
      .required(),
  };

  if (IsCampaignManager()) {
    if (localData.assigned_to && localData.assigned_to == "") {
      validationSchema.assigned_to = yup
        .string("Select assigned to")
        .required(t("createSession.assignFieldMandatory"));
    }
  }
  const formik = useFormik({
    initialValues: {
      method: "",
      assigned_to: localStorage.cerateSessionFormData?.assigned_to
        ? localStorage.cerateSessionFormData?.assigned_to
        : "",
      coacheeId: "",
      framework: "",
      session_type_id: "",
      subjects: "",
      projectTargetkpi: "",
    },
    validationSchema: yup.object(validationSchema),
    onSubmit: values => {
      isUpdate ? handleSubmitUpdate() : handleSubmitNew();
    },
  });

  const prevFormState = usePrevious(formState);

  // helper method to handle form changes
  // values can only be changed if it's not loading and the form is not locked.
  // values can be forcibly changed if force=true.
  const handleFormChange = (key, value, force = false) => {
    //restFormValues();

    if (force || (!isLoading && !formLocked)) {
      let newFormState = _.cloneDeep(formState);
      newFormState[key] = value;
      setFormState(newFormState);

      if (selectedCampaign && selectedCampaign !== "") {
        newFormState["campaign_id"] = selectedCampaign;
      }

      localStorage.setItem(
        `cerateSessionFormData`,
        JSON.stringify(newFormState)
      );
    }
  };

  useEffect(() => {
    setTimeout(() => {
      if (!selectedCampaign || selectedCampaign === "") {
        setSelectedCampaign(_.toInteger(localData?.campaign_id));
      }
      if (localData?.session_type_id && localData?.session_type_id !== "") {
        setDisableSubjects(false);
        formik.setFieldValue("session_type_id", localData.session_type_id);
        fetchSubjectResultMetrics();
      }
      if (localData.subjects && localData.subjects.length > 0) {
        const subjList = [];
        for (const item of localData.subjects) {
          subjList.push({
            subject_id: item.subject_id,
            metrics: item.result_metrics,
            occurrence: item.occurrence,
            link: item.document_link,
            project_target_kpi: item.project_target_kpi,
          });
        }
        setSubjectList(subjList);
        formik.setFieldValue("subjects", subjList);
      }
      if (localData.coachee_id && localData.coachee_id !== "") {
        formik.setFieldValue("coacheeId", localData.coachee_id);
      }
      if (localData.method && localData.method !== "") {
        formik.setFieldValue("method", localData.method);
      }
      if (localData.framework && localData.framework !== "") {
        formik.setFieldValue("framework", localData.framework);
      }
    }, 2000);
  }, []);

  //Update session value to form
  useEffect(() => {
    let newFormState = _.cloneDeep(formState);
    newFormState["duration"] = sessionDuration;
    setFormState(newFormState);
  }, [sessionDuration]);

  //Update session value to form
  useEffect(() => {
    if (selectedCampaign) {
      let newFormState = _.cloneDeep(formState);
      newFormState["campaign_id"] = selectedCampaign;
      newFormState["coachee_id"] = "";
      newFormState["subjects"] = [];
      //localStorage.setItem(`session-form-campaign_id`, selectedCampaign);
      setFormState(newFormState);

      if (localStorage.cerateSessionFormData) {
        const tempSessionFormLocalData = JSON.parse(
          localStorage.cerateSessionFormData
        );
        tempSessionFormLocalData.campaign_id = selectedCampaign;
        tempSessionFormLocalData.coachee_id = "";
        tempSessionFormLocalData.subjects = [];
        localStorage.setItem(
          `cerateSessionFormData`,
          JSON.stringify(tempSessionFormLocalData)
        );
        console.log("Form state", newFormState);
        console.log("New Local", localStorage.cerateSessionFormData);
      }
    }
  }, [selectedCampaign]);

  //Handle PD Flow
  useEffect(() => {
    if (isPDFlow) {
      handleFormChange("session_type_id", "2");
      formik.setFieldValue("session_type_id", "2");
    } else {
      const tempLocalData = JSON.parse(localStorage.cerateSessionFormData);

      if (tempLocalData && tempLocalData.framework === "1") {
        handleFormChange("session_type_id", "3");
        formik.setFieldValue("session_type_id", "3");
      }
    }
  }, [isPDFlow]);

  // pre-fill form if on update mode
  useEffect(() => {
    async function fetchSessionDetails() {
      const response = await apiRequest
        .get(`/api/getSessionDetails?session_id=${id}`)
        .catch(error => { });
      const data = response.data.data;

      // Begin pre-filling
      const sessionData = data["session"];
      const keys = Object.keys(sessionData);
      let newFormState = _.cloneDeep(formState);
      // for each field key value pair..
      keys.forEach(key => {
        if (formState.hasOwnProperty(key)) {
          let fieldValue = sessionData[key];
          if (key === "coach_id") {
            // if coach ID is different from current user, set assignToOtherCoach to true
            if (parseInt(fieldValue) !== parseInt(getLocalValue("uid"))) {
              setAssignToOtherCoach(true);
            }
          }

          // converting from utc to local timezone
          if (key.includes("datetime")) {
            fieldValue = new Date(
              dayjs(fieldValue).add(dayjs().utcOffset(), "minutes").format()
            );
          }

          // convert session type id to string
          if (key === "session_type_id") {
            fieldValue = `${fieldValue}`;
          }
          newFormState[key] = fieldValue;
        }
      });
      // update formState to updated prefilled values
      setFormState(newFormState);

      return true;
    }

    // main invocation
    if (isUpdate) {
      setIsLoading(true);
      fetchSessionDetails()
        .then(fetchSuccess => {
          if (fetchSuccess) {
            fetchSessionNotes();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  useEffect(() => {
    // fetch coaches list
    if (selectedCampaign && assignToOtherCoach) {
      apiRequest
        .get(`/api/getCoaches`, {
          params: {
            uid: getLocalValue("uid"),
            level_id: getLocalValue("u_levelid"),
            campaign_id: selectedCampaign,
          },
        })
        .then(resp => {
          let data = resp.data && resp.data.data ? resp.data.data : [];
          if (data.length > 0) {
            data = data.map(item => {
              return {
                value: item.uid,
                label: item.u_fullname,
                whitelist: item.whitelist,
                ...item,
              };
            });

            setCoachesList(data);
          }
        })
        .catch(error => { })
        .finally(() => { });
    }
  }, [assignToOtherCoach]);

  useEffect(() => {
    // fetch coaches list

    setCoacheeID(
      localStorage.getItem("session-form-coachee-id")
        ? localStorage.getItem("session-form-coachee-id")
        : ""
    );
    if (
      selectedCampaign &&
      (IsCampaignManager() || IsNormalCoach() || IsCoordinator())
    ) {
      if (formState.assigned_to) {
        let apiParam = {
          assigned_to: formState.assigned_to,
        };
        if (IsCampaignManager()) {
          apiParam.campaign_manager = true;
        }
        if (IsCoordinator()) {
          apiParam.coordinator = true;
        }

        if (IsNormalCoach()) {
          apiParam.normal_coach = true;
          //apiParam.campaign_manager = true;
        }

        getAgentList(apiParam);
      }
    } else if (selectedCampaign) {
      getAgentList();
    }

    //check if we ave coachee ata in local bd
    const tempLocalData = JSON.parse(localStorage.cerateSessionFormData);
    let newFormState = _.cloneDeep(formState);
    newFormState["subjects"] = [];
    setSubjectList([]);
    if (tempLocalData) {
      formik.setFieldValue("coacheeId", tempLocalData.coachee_id);
      newFormState["coachee_id"] = tempLocalData.coachee_id;
      setFormState(newFormState);
    } else {
      formik.setFieldValue("coacheeId", "");
      newFormState["coachee_id"] = "";
      setFormState(newFormState);
    }
  }, [selectedCampaign, formState.assigned_to]);

  const getAgentList = apiParam => {
    let agentParams = {
      campaign_id: selectedCampaign,
    };
    if (apiParam) {
      agentParams = { ...agentParams, ...apiParam };
    }

    // fetch coachees list
    apiRequest
      .get(`/api/agents`, {
        params: agentParams,
      })
      .then(resp => {
        let data = resp.data && resp.data.data ? resp.data.data : [];
        if (data.length > 0) {
          data = data.map(item => {
            return {
              value: item.uid,
              label: item.u_fullname,
              whitelist: item.whitelist,
              ...item,
            };
          });

          setCoacheesList(data);
        } else {
          setCoacheesList([]);
        }
      })
      .catch(error => { })
      .finally(() => { });
  };

  useEffect(() => {
    if (localData?.subjects && localData.subjects.length > 0) {
      fetchSubjectResultMetrics();
    }

    // fetch session types
    apiRequest
      .get(`/api/getSessionTypes`)
      .then(resp => {
        let data = resp.data && resp.data.data ? resp.data.data : [];
        if (data.length > 0) {
          setSessionTypes(data);
        }
      })
      .catch(error => { })
      .finally(() => { });
  }, []);

  useEffect(() => {
    if (formState.coachee_id && formState.framework) {
      let enabledSessionType = "";

      // Logic based on framework
      if (formState.framework === "1") {
        // PDCA
        enabledSessionType = "3";
      } else if (formState.framework === "2") {
        // PD
        enabledSessionType = "2"; // Enable Behavior and Conduct
      }

      // Set the session type based on the enabled value
      handleFormChange("session_type_id", enabledSessionType, true);
      formik.setFieldValue("session_type_id", enabledSessionType);
    }
  }, [formState.coachee_id, formState.framework, coacheesList]);

  // listen to changes in "coachee_id" in formState
  useEffect(() => {
    if (formState["coachee_id"] !== coacheeID) {
      setCoacheeID(formState["coachee_id"]);
      setSelectedScorecard(null);
    }
  }, [formState]);

  useEffect(() => {
    let requiredFields = ["framework", "session_type_id", "coachee_id"];

    if (IsCampaignManager()) {
      requiredFields.push("assigned_to");
    }

    // check if formstate's requiredfields have been changed
    let formIsDirty = false; // if any of the important(required) fields have changed
    let readyToFetch = true; // if there is sufficient info for API fetch
    let frameworkHasChanged = false; // if the framework field has changed
    if (formState && prevFormState) {
      requiredFields.forEach(key => {
        // check with previous formState if there is a change in an important(required) field
        if (formState[key] !== prevFormState[key]) {
          formIsDirty = true;
        }
        // readyToFetch will be false if any important field returns a falsy value
        if (!!!formState[key]) {
          readyToFetch = false;
        }
        // readyToFetch will be false if formState doesn't have any of the important fields
        if (!formState.hasOwnProperty(key)) {
          readyToFetch = false;
        }
        // if framework field has changed
        if (key === "framework" && formState[key] !== prevFormState[key]) {
          frameworkHasChanged = true;
        }
      });
    } else {
      // readyToFetch will be false if formState is not defined
      readyToFetch = false;
    }

    // disable add subjects button if not ready to fetch (missing important fields)
    if (formIsDirty) {
      if (!readyToFetch) {
        setDisableSubjects(true);
      }
    }

    // set PD flow
    // only triggers if there are no subjects in the list or if the form is locked
    if (formLocked || formState.subjects.length <= 0) {
      if (frameworkHasChanged) {
        setIsPDFlow(formState["framework"] === "2");

        // don't fetch when switching back to PDCA
        if (formState["framework"] === "1") {
          readyToFetch = false;
        }
      }
    }

    // if any important values were changed, and there are enough info to fetch subjects
    if (formIsDirty && readyToFetch) {
      // triggering confirmation dialog.
      // only trigger if formstate is not locked and there are subjects in the list
      if (!formLocked && formState.subjects.length > 0) {
        setFormLocked(true); // lock formstate to prevent any other automated changes
        setSavedFormState(prevFormState); // save previous form state to cache
        // open confirmation dialog
        setConfirmationDialogOpen(true);
      } else {
        // fetch subject result metrics if there are no subjects in the list
        setTimeout(() => {
          fetchSubjectResultMetrics();
        }, 2000);
      }
    }
  }, [formState]);

  // handle project target kpi
  useEffect(() => {
    // Assuming formState.session_type_id is the value you want to check
    if (formState.session_type_id === "2") {
      sethideProjectTargetKpi(true);
      sethideCurrentMetricsi(true);
    } else {
      sethideProjectTargetKpi(false);
      sethideCurrentMetricsi(false);
    }
  }, [formState.session_type_id]);
  // user selects "yes":
  //    - reset the subject list
  //    - refetch the subject selections list with fetchSubjectResultMetrics
  function handleResetConfirm() {
    setDisableDialogButtons(true); // disable dialog button
    // resetting subject list
    setSubjectList([]);
    handleFormChange("subjects", [], true);

    // make sure IsPDFlow is updated. force change session type if needed
    setIsPDFlow(formState["framework"] === "2");
    if (formState["framework"] === "2") {
      // Handling session type for framework 2
      handleFormChange("session_type_id", "2", true);
      formik.setFieldValue("session_type_id", "2");
    } else if (formState["framework"] === "1") {
      // Handling session type for framework 1
      if (formState["session_type_id" === "1"]) {
        handleFormChange("session_type_id", "3", true);
        formik.setFieldValue("session_type_id", "3");
      }
    }

    setTimeout(() => {
      setFormLocked(false); // unlocking form state
      setConfirmationDialogOpen(false); // close dialog
      setDisableDialogButtons(false); // re-enable dialog button
      fetchSubjectResultMetrics(); // re-fetch subject selections list
      setSavedFormState(null); // clear saved form state
    }, 300);
  }

  // user selects "no":
  //    - revert the formState to previous values
  function handleResetReject() {
    setDisableDialogButtons(true);
    setFormState(savedFormState); // reverting formstate to previous values
    setTimeout(() => {
      setFormLocked(false);
      setConfirmationDialogOpen(false);
      setDisableDialogButtons(false);
      setSavedFormState(null); // clear saved form state
    }, 300);
  }

  function fetchSubjectResultMetrics() {
    let metricsObj = {
      coachee_id: formState["coachee_id"],
      framework: formState["framework"],
      session_type_id: formState["session_type_id"],
      campaign_id: selectedCampaign,
    };

    //Pass campaign id only when is campaign manger

    if (IsCampaignManager()) {
      metricsObj.campaign_manager = IsCampaignManager();
      metricsObj.assigned_to = formState.assigned_to;
    }
    if (IsCoordinator()) {
      metricsObj.coordinator = IsCoordinator();
      metricsObj.assigned_to = formState.assigned_to;
    }
    if (IsNormalCoach()) {
      //Also needed for normal coach
      metricsObj.normal_coach = IsNormalCoach();
      metricsObj.assigned_to = formState.assigned_to;
    }

    apiRequest
      .get(`/api/getSubjectResultMetrics`, {
        params: metricsObj,
      })
      .then(resp => {
        let data = resp.data && resp.data.data ? resp.data.data : [];
        // transform data
        if (data.length > 0) {
          /**
           * The `value` must be unique across polymorphic subjects: 'id-type',
           * Since different subject types can share the same numeric id,
           * we combine `id` + `subjectable_type` to create a stable key.
           */
          data = data.map(item => {
            return {
              ...item,
              value: `${item.id}-${item.subjectable_type}`,
              label:
                item.subject +
                (item.sub_subject ? " — " + item.sub_subject : ""),
            };
          });
          setSubjectSelectionsList(data);
        } else {
          setSubjectSelectionsList([]);
        }
        setDisableSubjects(false);
      })
      .catch(error => { })
      .finally(() => { });
  }
  // on coaching date change, check if review date adheres to the constraint:
  //    "review date must be at least 2 days after coaching date"
  useEffect(() => {
    if (formState && prevFormState) {
      // convert both to unix time for comparison
      let curr = new Date(formState["session_datetime"]).getTime();
      let prev = new Date(prevFormState["session_datetime"]).getTime();
      // if there is a change in value
      if (curr !== prev) {
        let coachDate = formState["session_datetime"];
        let reviewDate = formState["review_datetime"];
        coachDate = new Date(coachDate).setHours(0, 0, 0, 0); // set hours to 00:00:00.000 am
        reviewDate = new Date(reviewDate).setHours(0, 0, 0, 0); // set hours to 00:00:00.000 am

        let coachDatePlusTwo = new Date(coachDate);
        coachDatePlusTwo.setDate(coachDatePlusTwo.getDate() + 2);

        // if the review date is found to be <2 days away from coaching date
        if (reviewDate < coachDatePlusTwo) {
          handleFormChange("review_datetime", coachDatePlusTwo); // forcibly change review date
        }
        setMinReviewDate(coachDatePlusTwo); // update minDate for review date field
        setMaxReviewDate(
          dayjs(coachDate).add(1, "month").endOf("month").toDate()
        );
      }
    }
  }, [formState]);

  // update coach id upon clicking on "assign to other coach"
  useEffect(() => {
    // only when it's not loading
    if (!isLoading) {
      if (assignToOtherCoach && coachesList.length > 0) {
      } else {
        handleFormChange("coach_id", getLocalValue("uid"));
      }
    }
  }, [assignToOtherCoach, coachesList]);

  // update subjects list on formState upon change
  useEffect(() => {
    let newSubjects = subjectList.map(subject => {
      return {
        subject_id: subject.subject_id,
        subjectable_type: subject.subjectable_type,
        result_metrics: subject.metrics,
        mtd_metric: subject.mtd_metric,
        project_target_kpi: subject.project_target_kpi,
        occurrence: 0,
        document_link: subject.link,
      };
    });
    if (
      newSubjects &&
      newSubjects.length > 0 &&
      newSubjects[0].result_metrics !== "" &&
      newSubjects[0].result_metrics !== 0
    ) {
      formik.setFieldValue("subjectMetric", newSubjects[0].result_metrics);
    }

    if (
      newSubjects &&
      newSubjects.length > 0 &&
      newSubjects[0].project_target_kpi !== "" &&
      newSubjects[0].project_target_kpi !== 0
    ) {
      formik.setFieldValue(
        "projectTargetkpi",
        newSubjects[0].project_target_kpi
      );
    }

    handleFormChange("subjects", newSubjects);
    formik.setFieldValue("subjects", newSubjects);
  }, [subjectList]);

  // fetch session notes
  const fetchSessionNotes = async function () {
    const response = await apiRequest
      .get(`/api/getSessionNotes?session_id=${id}`)
      .catch(error => { });

    if (response.data && response.data.data && response.data.data.notes) {
      const notes = response.data.data.notes;
      let newNoteList = [];
      notes.forEach(note => {
        note.type_id = note.note_category_id;
        newNoteList.push({
          id: note.note_id,
          type_id: note.type_id,
          note: note.note,
          like: false, // messages are unliked by default
          author: note.note_created_user
            ? note.note_created_user
            : getLocalValue("u_fullname"),
          note_created_at: note.note_created_at,
        });
      });
      setNoteList(newNoteList);
    }
  };

  // handle session note sending
  const handleSessionNoteSend = (
    text,
    noteTypeObj,
    successCallback,
    note_created_at
  ) => {
    if (isUpdate) {
      let sessionNotesObj = {
        session_id: id,
        note_category_id: noteTypeObj.type_id,
        note: text,
        user_type: getLocalValue("u_role"),
        created_by: getLocalValue("uid"),
        liked_by_coach: 0, // messages are unliked by default
        liked_by_coachee: 0,
        note_created_at: new Date(),
      };

      apiRequest
        .post(`/api/createSessionNotes`, sessionNotesObj)
        .then(resp => {
          if (resp.status === 200) {
            fetchSessionNotes();
            successCallback(true);
          }
        })
        .catch(error => {
          addNotification(`${error}`, "error");
        })
        .finally(() => { });
    } else {
      appendSessionNote(text, noteTypeObj);
      successCallback(true);
    }
  };

  // appends a single session note. only updates `noteList` and not formState
  const appendSessionNote = (text, noteTypeObj) => {
    let newNoteList = noteList;
    newNoteList.push({
      id: noteCounter, // random counter value
      type_id: noteTypeObj.type_id,
      note: noteTypeObj.note ? noteTypeObj.note : text,
      like: false, // messages are unliked by default
      author: noteTypeObj.note_created_user
        ? noteTypeObj.note_created_user
        : getLocalValue("u_fullname"),
      note_created_at: new Date().toISOString(),
    });
    setNoteList(newNoteList);
    setNoteCounter(noteCounter + 1);
  };

  // update formState upon changing noteList
  useEffect(() => {
    let formNoteList = noteList.map(o => {
      return {
        note_category_id: o.type_id,
        note: o.note,
        liked_by_coach: o.like ? 1 : 0, // create session page is only accessible by coaches
        liked_by_coachee: 0,
        note_created_at: o.note_created_at,
      };
    });
    handleFormChange("notes", formNoteList);
  }, [noteList.length, noteList]);

  // handle form submission
  const handleSubmitNew = () => {
    let formSnapshot = _.cloneDeep(formState);

    // convert 'id-type' back to numeric id before sending to BE.
    formSnapshot.subjects = formSnapshot.subjects.map((subject) => {
      return {
        ...subject,
        subject_id: subject.subject_id.replace(`-${subject.subjectable_type}`, ''),
      };
    })

    // reformatting dates to UTC (YYYY-MM-DD HH:mm:ss)
    formSnapshot.session_datetime = dayjs(formSnapshot.session_datetime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    formSnapshot.review_datetime = dayjs(formSnapshot.review_datetime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    formSnapshot.campaign_id = selectedCampaign;
    if (selectedScorecard) {
      formSnapshot.scorecard_id = selectedScorecard.scorecard_id;
    }
    // console.log("Post data", formSnapshot);
    // return;
    setIsLoading(true);
    if (parseInt(formSnapshot.framework) === Framework.PD) {
      formSnapshot.review_datetime = null;
    }

    apiRequest
      .post(`/api/createSession`, formSnapshot)
      .then(resp => {
        if (resp.status === 200 && resp.data && resp.data.data) {
          setIsLoading(false);
          let createdSessionID = resp.data.data;
          if (selectedScorecard) {
            ///Update score card application with selected api
            const formParam = {
              scoreCardId: selectedScorecard.scorecard_id,
              userId: coacheeID,
              sessionId: createdSessionID,
            };
            updateScorecardApp(formParam);
          } else {
            history.push(`/sessions/details/${createdSessionID}`);
          }
          restFormValues();
        }
      })
      .catch(error => {
        setIsLoading(false);

        addNotification(`${error}`, "error");
      });
  };

  //Handle scorecard app update
  const updateScorecardApp = formParam => {
    scorecardApiRequest
      .post(`/scorecard/updateSessionId`, formParam)
      .then(resp => {
        history.push(`/sessions/details/${formParam.sessionId}`);
      })
      .catch(error => {
        addNotification(`${error}`, "error");
      });
  };

  // handle form submission
  const handleSubmitUpdate = () => {
    let formSnapshot = _.cloneDeep(formState);

    // reformatting dates to UTC (YYYY-MM-DD HH:mm:ss)
    formSnapshot.session_datetime = dayjs(formSnapshot.session_datetime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    formSnapshot.review_datetime = dayjs(formSnapshot.review_datetime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    formSnapshot.session_id = id;
    if (selectedScorecard) {
      formSnapshot.scorecard_id = selectedScorecard.scorecard_id;
    }

    setIsLoading(true);
    apiRequest
      .post(`/api/editSession`, formSnapshot)
      .then(resp => {
        if (resp.status === 200) {
          setIsLoading(false);
          history.push(`/sessions/details/${id}`);
        }
      })
      .catch(error => {
        setIsLoading(false);

        addNotification(`${error}`, "error");
        restFormValues();
      })
      .finally(() => { });
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  const viewCoacheeStates = () => {
    const selectedCoachee = formState["coachee_id"];
    if (selectedCoachee !== "") {
      setViewStates(true);
    } else {
      formik.setFieldError("coacheeId", t("error.pleaseSelectCoachee"), true);
      formik.setFieldTouched("coacheeId", true, true);
    }
  };

  return (
    <DashboardLayout
      appBarContent={
        <Stepper
          phase={t("header.stepper.plan")}
          phaseLabel="Check"
          sessionId={null}
          clockRun={true}
          getTotalTimeSpent={getTotalTime}
        />
      }
      contentClass={styles.dashboardLayout__content}
    >
      <AlertDialog
        open={confirmationDialogOpen}
        onClose={() => {
          setConfirmationDialogOpen(false);
        }}
        title={`Change session details?`}
        body={
          "You are changing the coachee, framework, and/or session type. Your subjects will be reset."
        }
        footer={
          <>
            <Button
              disabled={disableDialogButtons}
              onClick={() => handleResetConfirm()}
            >
              Yes, change
            </Button>
            <Button
              disabled={disableDialogButtons}
              color="primary"
              onClick={() => handleResetReject()}
            >
              No, keep current details
            </Button>
          </>
        }
        disableBackdropClick
      />
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <BackButton text={t("common.backToSession")} />
            <SectionTitle>
              {isUpdate
                ? "Update session"
                : t("createSession.createNewSession")}
            </SectionTitle>
            <div className={styles.detailsContainer}>
              <div className={styles.row}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.campaign")}
                </div>
                <div className={styles.rowRight}>
                  <CampaignList variant="outlined" isCreateSession={true} />
                </div>
              </div>
              <div className={styles.row}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.coach")}
                </div>
                <div className={styles.rowRight}>
                  <div className={styles.assignToOtherCoach__checkbox}>
                    <Checkbox
                      checked={assignToOtherCoach}
                      onChange={e => {
                        setAssignToOtherCoach(e.target.checked);
                      }}
                    />
                    {t("createSession.assignToOtherCoach")}
                  </div>

                  {assignToOtherCoach && (
                    <Select
                      className={styles.dropdownSelect}
                      placeholder="Select a coach"
                      searchable={true}
                      list={coachesList}
                      value={formState["coach_id"]}
                      onChange={e => {
                        handleFormChange("coach_id", e.target.value);
                      }}
                    />
                  )}
                </div>
              </div>

              {/* new Request from client - Campaign Manager should be able to coach team lead and agents. */}
              {(IsCampaignManager() || IsNormalCoach() || IsCoordinator()) && (
                <div className={ClassNames(styles.row)}>
                  <div
                    className={styles.rowLeft}
                    style={{
                      paddingTop: "12px",
                    }}
                  >
                    {t("createSession.assignTo")}
                  </div>
                  <div className={styles.rowRight}>
                    <FormControl
                      style={{ width: "100%" }}
                      error={
                        formik.touched.assigned_to &&
                        Boolean(formik.errors.assigned_to)
                      }
                    >
                      <RadioGroup
                        style={{ flexDirection: "row" }}
                        value={formState["assigned_to"]}
                        name="assigned_to"
                        onChange={e => {
                          handleFormChange("assigned_to", e.target.value);
                          formik.handleChange(e);
                        }}
                      >
                        <FormControlLabel
                          value="coach"
                          control={<Radio />}
                          label="Coach"
                        />
                        <FormControlLabel
                          value="coachee"
                          control={<Radio />}
                          label="Coachee"
                        />
                      </RadioGroup>
                      {formik.touched["assigned_to"] &&
                        formik.errors["assigned_to"] && (
                          <FormHelperText>
                            {formik.errors["assigned_to"]}
                          </FormHelperText>
                        )}
                    </FormControl>
                  </div>
                </div>
              )}
              <div className={styles.row}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.coachee")}
                </div>
                <div className={styles.rowRight}>
                  <div>
                    <Select
                      className={styles.dropdownSelect}
                      placeholder="Select a team member"
                      displayEmpty
                      searchable={true}
                      list={coacheesList}
                      value={coacheeID}
                      onChange={e => {
                        const coacheeId = e.target.value;
                        handleFormChange("coachee_id", coacheeId);
                        formik.setFieldValue("coacheeId", coacheeId);
                      }}
                      name="coacheeId"
                      error={
                        formik.touched.coacheeId &&
                        Boolean(formik.errors.coacheeId)
                      }
                      helperText={
                        formik.touched.coacheeId && formik.errors.coacheeId
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={ClassNames(styles.row, styles.mediumGutters)}>
                <div className={styles.rowLeft} />
                <div className={styles.rowRight}>
                  <ButtonPill
                    className={styles.coacheeStatusButton}
                    prepend={
                      <EqualizerIcon
                        fontSize="inherit"
                        style={{ marginRight: "4px" }}
                      />
                    }
                    color="inherit"
                    onClick={() => {
                      viewCoacheeStates();
                    }}
                  >
                    {t("createSession.viewCoacheesStates")}
                  </ButtonPill>
                </div>
              </div>
              <div className={ClassNames(styles.row, styles.mediumGutters)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.coachingDate")}
                </div>
                <div className={styles.rowRight}>
                  <DatePickerGroup
                    disablePast
                    selectedDate={formState["session_datetime"]}
                    handleDateChange={newDate => {
                      handleFormChange("session_datetime", newDate);
                    }}
                  />
                </div>
              </div>
              <div className={ClassNames(styles.row)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.mode")}
                </div>
                <div className={styles.rowRight}>
                  <FormControl
                    style={{ width: "100%" }}
                    error={
                      localData?.method && localData?.method !== ""
                        ? null
                        : formik.touched.method && Boolean(formik.errors.method)
                    }
                  >
                    <RadioGroup
                      style={{ flexDirection: "row" }}
                      value={formState["method"]}
                      name="method"
                      onChange={e => {
                        handleFormChange("method", e.target.value);
                        formik.handleChange(e);
                      }}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label="Online"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio />}
                        label="On-site"
                      />
                    </RadioGroup>
                    {formik.touched["method"] &&
                      formik.errors["method"] &&
                      (!localData?.method || localData?.method === "") && (
                        <FormHelperText>
                          {formik.errors["method"]}
                        </FormHelperText>
                      )}
                  </FormControl>
                </div>
              </div>
              <div className={ClassNames(styles.row)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.framework")}
                </div>
                <div className={styles.rowRight}>
                  <FormControl
                    style={{ width: "100%" }}
                    error={
                      localData?.framework && localData?.framework !== ""
                        ? null
                        : formik.touched.framework &&
                        Boolean(formik.errors.framework)
                    }
                  >
                    <RadioGroup
                      style={{ flexDirection: "row" }}
                      value={
                        formState["framework"] ? formState["framework"] : ""
                      }
                      name="framework"
                      onChange={e => {
                        handleFormChange("framework", e.target.value);
                        formik.handleChange(e);
                      }}
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label="PDCA"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio />}
                        label="PD"
                      />
                    </RadioGroup>
                    {formik.touched["framework"] &&
                      formik.errors["framework"] &&
                      (!localData?.framework ||
                        localData?.framework === "") && (
                        <FormHelperText>
                          {formik.errors["framework"]}
                        </FormHelperText>
                      )}
                  </FormControl>
                </div>
              </div>
              <div className={ClassNames(styles.row, styles.mediumGutters)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.reviewDate")}
                </div>
                <div className={styles.rowRight}>
                  <DatePickerGroup
                    minDate={minReviewDate}
                    maxDate={maxReviewDate}
                    disabled={isPDFlow}
                    disablePast
                    selectedDate={formState["review_datetime"]}
                    handleDateChange={newDate => {
                      handleFormChange("review_datetime", newDate);
                    }}
                  />
                </div>
              </div>
              <div className={ClassNames(styles.row, styles.mediumGutters)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.typeOfSession")}
                </div>
                <div className={styles.rowRight}>
                  {formState && (
                    <FormControl
                      style={{ width: "100%" }}
                      error={
                        !localData?.session_type_id
                          ? null
                          : formik.touched.session_type_id &&
                          Boolean(formik.errors.session_type_id)
                      }
                    >
                      <RadioGroup
                        style={{ flexDirection: "row" }}
                        name="session_type_id"
                        value={
                          formState["session_type_id"]
                            ? formState["session_type_id"]
                            : ""
                        }
                        onChange={e => {
                          handleFormChange("session_type_id", e.target.value);
                          formik.handleChange(e);
                        }}
                      >
                        {sessionTypes.map((item, index) => {
                          return (
                            <>
                              <FormControlLabel
                                key={index}
                                value={`${item.id}`}
                                disabled={
                                  localData?.framework !== ""
                                    ? isPDFlow
                                      ? (item.id === 1 && isPDFlow) ||
                                      (item.id === 3 && isPDFlow)
                                      : item.id === 2 && !isPDFlow
                                    : item.id === 1 && isPDFlow
                                }
                                control={<Radio />}
                                label={item.type}
                              />
                            </>
                          );
                        })}
                      </RadioGroup>
                      {formik.touched["session_type_id"] &&
                        formik.errors?.session_type_id &&
                        !localData?.session_type_id && (
                          <FormHelperText error={true}>
                            {formik.errors?.session_type_id}
                          </FormHelperText>
                        )}
                    </FormControl>
                  )}
                </div>
              </div>
              <div className={ClassNames(styles.row, styles.largeGutters)}>
                <div
                  className={styles.rowLeft}
                  style={{
                    paddingTop: "12px",
                  }}
                >
                  {t("createSession.subjects")}
                </div>
                <div className={styles.rowRight}>
                  <FormControl
                    style={{ width: "100%" }}
                    name="subjects"
                    error={
                      (formik.touched.subjects &&
                        Boolean(formik.errors.subjects)) ||
                      (formik.touched.subjectMetric &&
                        Boolean(formik.errors.subjectMetric)) ||
                      (formik.touched.projectTargetkpi &&
                        Boolean(formik.errors.projectTargetkpi))
                    }
                  >
                    <SubjectListInput
                      sessionTypeId={localData?.session_type_id}
                      list={subjectList}
                      name="subjects"
                      disabled={disableSubjects}
                      setList={setSubjectList}
                      coacheeId={formState.coachee_id}
                      coachId={formState.coach_id}
                      subjectDropdownList={subjectSelectionsList}
                      onChange={formik.handleChange}
                      hideProjectTargetKpi={hideProjectTargetKpi}
                      hideCurrentMetrics={hideCurrentMetrics}
                      onDelete={() => {
                        formik.setFieldValue("subjects", "");
                      }}
                      subjectError={formik.errors?.subjects}
                      projectTargetKpiError={false}
                      showError={formik.touched}
                    />

                    {formik.touched?.subjects &&
                      formik.touched?.subjects.length === 0 &&
                      formik.errors?.subjects &&
                      !Array.isArray(formik.errors?.subjects) && (
                        <FormHelperText error={true}>
                          {"Please add at least one subject"}
                        </FormHelperText>
                      )}
                  </FormControl>
                </div>
              </div>
            </div>
          </Section>
        </PageContent>
        <PageContent>
          <div className={styles.sessionNotesSection}>
            <div className={styles.detailsContainer}>
              <div className={styles.row}>
                <div className={styles.rowLeft} style={{ paddingTop: "12px" }}>
                  {t("createSession.sessionNotes")}
                </div>
                <div
                  className={ClassNames(
                    styles.rowRight,
                    styles.sessionNotesWrapper
                  )}
                >
                  <NotesInputGroup handleSend={handleSessionNoteSend} />
                  <div className={styles.sessionNotesDivider}></div>
                  <NotesList list={noteList} setList={setNoteList} />
                </div>
              </div>
            </div>
          </div>
        </PageContent>
        <PageContent indent>
          <Section>
            <div className={styles.detailsContainer}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                isloading={isLoading ? 1 : 0}
                //  onClick={isUpdate ? handleSubmitUpdate : handleSubmitNew}
                onClick={() => {
                  formik.handleSubmit();
                  scrollToErrors(formik);
                }}
              >
                {isUpdate
                  ? "Update Session"
                  : t("createSession.createSendInvitation")}
              </Button>
            </div>
          </Section>
        </PageContent>

        <AlertDialog
          title={"Logs"}
          body={
            <LogsTable
              getDataFrom={"/api/getPersonalSessionLogs"}
              uid={formState["coachee_id"]}
            />
          }
          footer={
            <Button onClick={() => setViewStates(false)}>
              {t("createSession.close")}
            </Button>
          }
          fullWidth={true}
          maxWidth={"md"}
          open={viewStates}
          onClose={val => { }}
        />
      </div>
    </DashboardLayout>
  );
}
